/* ------------------- IMPORTS */
import styled from '@emotion/styled'
import Utils from 'api/Utils'
import EmptyState from '../UI/EmptyState'
import SkeletonCard from '../UI/SkeletonCard'
import PreviewCard from './PreviewCard'
import { AnimatedPlaceholderLogo, Box } from 'components'
import { useEffect, useRef, useState } from 'react'
import { getArticlebyTopicList } from '../commonFunction'
import { useDiscoverStore } from 'stores/DiscoverStore'
import { IArticle } from '../aiDiscoverInterface'
import { LoadArticlesButton } from '../UI/LoadArticlesButton'
import { AppStore } from 'utils'
import { MAX_ARTICLE_NUMBER } from 'utils/Constants/Constants'

export const TrendArticles = () => {
  /* ----------------- STATE  */
  const [canLoadMoreArticles, setCanLoadMoreArticles] = useState<boolean>()
  const [isLoadingNewArticles, setIsLoadingNewArticles] = useState<boolean>(false)
  const [showLoadingAnimation, setShowLoadingAnimation] = useState(false)

  /* ----------------- ZUSTAND  */
  const {
    trendingArticlesByTopicList,
    setTrendingArticlesByTopicList,
    dateFilterFollow,
    languageFilterFollow,
    trendingArticlesByTopicListPage,
    setTrendingArticlesByTopicListPage,
    numberOfFollowedTopic,
    setNumberOfFollowedTopic,
  } = useDiscoverStore((state) => ({
    trendingArticlesByTopicList: state.trendingArticlesByTopicList,
    setTrendingArticlesByTopicList: state.setTrendingArticlesByTopicList,
    dateFilterFollow: state.dateFilterFollow,
    languageFilterFollow: state.languageFilterFollow,
    trendingArticlesByTopicListPage: state.trendingArticlesByTopicListPage,
    setTrendingArticlesByTopicListPage: state.setTrendingArticlesByTopicListPage,
    numberOfFollowedTopic: state.numberOfFollowedTopic,
    setNumberOfFollowedTopic: state.setNumberOfFollowedTopic,
  }))

  /* ----------------- VARIABLES  */
  const articleNumber = MAX_ARTICLE_NUMBER
  const startDate: string | undefined =
    dateFilterFollow[0] !== undefined ? dateFilterFollow[0].format('YYYY-MM-DD') : undefined
  const endDate: string | undefined =
    dateFilterFollow[1] !== undefined ? dateFilterFollow[1].format('YYYY-MM-DD') : undefined
  let updatedArticlesList = trendingArticlesByTopicList
  const followedTopicList = AppStore.discoverUser?.topicsId ?? []

  /* ----------------- REF  */
  const isFirstRender = useRef(true)
  const loaderRef = useRef<HTMLDivElement>(null)
  /* ----------------- METHODS  */
  //Controlla se è possibile caricare altri articoli
  const checkIsPossibleLoadingMoreArticles = () => {
    if (updatedArticlesList) {
      updatedArticlesList.length % articleNumber === 0 ? setCanLoadMoreArticles(true) : setCanLoadMoreArticles(false)
    }
  }

  const handleOnClick = () => {
    // Questo fa partire la chiamata con la chiamata successiva
    setTrendingArticlesByTopicListPage(trendingArticlesByTopicListPage + 1)
  }

  /* ----------------- API CALL  */
  const fetchTrendingArticles = async () => {
    setShowLoadingAnimation(true)
    setIsLoadingNewArticles(true)
    try {
      const newArticles: IArticle[] = await getArticlebyTopicList(
        trendingArticlesByTopicListPage,
        MAX_ARTICLE_NUMBER,
        startDate,
        endDate,
        languageFilterFollow[0]
      )

      setTrendingArticlesByTopicList(
        updatedArticlesList ? [...trendingArticlesByTopicList!, ...newArticles!] : newArticles
      )

      updatedArticlesList = updatedArticlesList ? [...trendingArticlesByTopicList!, ...newArticles!] : newArticles

      checkIsPossibleLoadingMoreArticles()
    } catch (e) {}
    setIsLoadingNewArticles(false)
  }

  /* ----------------- USEEFFECT  */
  //Primo render che fetcha i dati
  useEffect(() => {
    if (trendingArticlesByTopicList === null || trendingArticlesByTopicList.length === 0) {
      setTrendingArticlesByTopicList(null)
      updatedArticlesList = null
      setNumberOfFollowedTopic(followedTopicList.length)
      fetchTrendingArticles()
    } else if (followedTopicList.length !== numberOfFollowedTopic) {
      setTrendingArticlesByTopicList(null)
      updatedArticlesList = null
      setNumberOfFollowedTopic(followedTopicList.length)
      fetchTrendingArticles()
    }
    checkIsPossibleLoadingMoreArticles()
  }, [])

  // UseEffect per fetchare i dati delle nuove pagine
  useEffect(() => {
    if (isFirstRender.current) {
    } else {
      fetchTrendingArticles()
    }
  }, [trendingArticlesByTopicListPage])

  //Reset dei risultati al cambio del filtri
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
    } else {
      setTrendingArticlesByTopicList(null)
      updatedArticlesList = null
      setTrendingArticlesByTopicListPage(1)
      fetchTrendingArticles()
    }
  }, [languageFilterFollow[0], dateFilterFollow[0], dateFilterFollow[1]])

  const loadMoreArticles = (entries: IntersectionObserverEntry[]): void => {
    const target = entries[0] // Ottiene il primo elemento osservato dall'IntersectionObserver

    if (target.isIntersecting && canLoadMoreArticles && !isLoadingNewArticles) {
      handleOnClick()
    }
  }

  useEffect(() => {
    // Crea un nuovo IntersectionObserver che chiama 'loadMoreArticles' quando
    // l'elemento riferito da 'loaderRef' interseca la viewport
    const observer = new IntersectionObserver(loadMoreArticles)

    // Se 'loaderRef.current' è definito (ovvero se il componente è montato),
    // inizia ad osservare questo elemento
    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    // Alla dismissione del componente, interrompe l'osservazione
    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current)
      }
    }
  }, [canLoadMoreArticles, isLoadingNewArticles])

  return (
    <TrendArticlesContainer>
      {/* Mentre la chiamata è in corso mostra skeleton*/}
      {trendingArticlesByTopicList === null && (
        <SkeletonCard key={Utils.generateRandomUIDKey()} type="article" num={9} />
      )}

      {/* Risultato se c'è del contenuto*/}
      {trendingArticlesByTopicList &&
        trendingArticlesByTopicList.length > 0 &&
        trendingArticlesByTopicList.map((article, i) => (
          <PreviewCard key={i + article.uri} article={article} index={i} />
        ))}

      {/* caricamento di nuovi articoli scendendo in basso dalla pagina */}
      {showLoadingAnimation && trendingArticlesByTopicList && trendingArticlesByTopicList.length > 0 && (
        <AnimatedPlaceholderLogo containerStyle={{ backgroundColor: 'transparent', padding: '5px' }} />
      )}
      <div ref={loaderRef} />

      {/* Risultato se non c'è del trendingArticlesByTopicList  */}
      {trendingArticlesByTopicList !== null && trendingArticlesByTopicList.length === 0 && (
        <EmptyState type={'follow'} />
      )}
    </TrendArticlesContainer>
  )
}

const TrendArticlesContainer = styled(Box)`
  margin: 24px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-content: flex-start;
  gap: 39.5px;

  &::-webkit-scrollbar {
    display: none;
  }
`
const LoadArticles = styled(Box)`
  display: 'flex';
  justify-content: 'center';
  align-items: 'center';
`
